import { useState } from 'react';
import Markdown from './Markdown';
import { StatusPill } from './ui';
import { formatDateTime, gradeTone } from '../lib/format';
import { CARD, CARD_STYLE } from '../lib/ui';
import type { RatingReport } from '../types/workspace';

const TONE_COLOR = { green: '#059669', amber: '#d97706', rose: '#e11d48', slate: '#64748b', pink: '#d6006e', blue: '#2563eb' };

export function GradeDial({ grade, score, size = 116 }: { grade: string; score: number; size?: number }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  const color = TONE_COLOR[gradeTone(score)];
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 110 110" className="h-full w-full -rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#fce7f3" strokeWidth="9" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeDasharray={`${(score / 100) * c} ${c}`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black tracking-tight text-[#6b0030]">{grade}</span>
        <span className="text-[11px] font-bold text-[#c084a0]">{score}/100</span>
      </div>
    </div>
  );
}

export default function RatingReportView({ report, defaultOpen = false }: { report: RatingReport; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const strengths = report.strengths || [];
  const risks = report.risks || [];

  return (
    <article className={`${CARD} overflow-hidden`} style={CARD_STYLE}>
      <div className="flex flex-wrap items-center gap-5 p-5" style={{ background: 'linear-gradient(135deg, #fff5f8 0%, #ffffff 70%)' }}>
        <GradeDial grade={report.grade} score={report.score} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill label={`Version ${report.version}`} tone="pink" />
            {report.outlook && <StatusPill label={`${report.outlook} outlook`} tone={report.outlook.toLowerCase().startsWith('neg') ? 'rose' : 'green'} />}
            <span className="text-[11px] text-[#c084a0]">{formatDateTime(report.createdAt)}</span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-[#4a1a30]">{report.summary}</p>
          <p className="mt-2 text-[11px] text-[#c084a0]">Prepared by {report.preparedByLabel || 'Micus Assessment Team'}</p>
        </div>
      </div>

      {report.scoreBreakdown && (
        <div className="border-t border-[#fce7f3] bg-white p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#d6006e]">Score breakdown</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {report.scoreBreakdown.categories.map((category) => (
              <div key={category.key} className="rounded-xl border border-[#fce7f3] p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-[#6b0030]">{category.label}</p>
                  <span className="text-xs font-bold text-[#d6006e]">{category.pointsEarned ?? '—'}/{category.pointsAvailable}</span>
                </div>
                <p className="mt-1 text-xs text-[#9a6b82]">{category.weight}% weight · {category.verdict || `${category.completenessScore ?? '—'}% complete`}</p>
                {category.verdict === 'INSUFFICIENT' && <p className="mt-1 text-[11px] font-semibold text-amber-700">Evidence completeness only — not a judgment of business strength.</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(strengths.length > 0 || risks.length > 0) && (
        <div className="grid gap-px bg-[#fce7f3] sm:grid-cols-2">
          <div className="bg-white p-5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#047857]">Strengths</p>
            <ul className="mt-2 space-y-1.5">
              {strengths.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm text-[#4a1a30]">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#be123c]">Key risks</p>
            <ul className="mt-2 space-y-1.5">
              {risks.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm text-[#4a1a30]">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {report.reportMarkdown && (
        <div className="border-t border-[#fce7f3]">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#d6006e] hover:bg-[#fff5f8]"
          >
            Full rating report
            <span>{open ? '−' : '+'}</span>
          </button>
          {open && (
            <div className="px-5 pb-5">
              <Markdown source={report.reportMarkdown} />
            </div>
          )}
        </div>
      )}
    </article>
  );
}
